import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Drawer, Typography, useMediaQuery, IconButton, Tooltip,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import { useHistory, useLocation } from 'react-router-dom';
import { IPD_COLORS, IPD_SIDEBAR, INV_RADIUS, thinScrollbarSx, hideScrollbarSx } from '../theme';
import { NAV_MODULES, resolveActiveNavId } from '../navigation';
import { isFocusRoute } from '../utils/layoutRoutes';

const SIDEBAR_EXPANDED = 248;
const SIDEBAR_COLLAPSED = 72;
const STORAGE_KEY = 'inv_sidebar_expanded';

function readExpandedPreference(key) {
  try {
    const stored = sessionStorage.getItem(key);
    if (stored === 'true') return true;
    if (stored === 'false') return false;
  } catch { /* ignore */ }
  return true;
}

function NavItem({ item, active, collapsed, badge, onNav }) {
  const NavIcon = item.icon;
  const disabled = !!item.disabled;

  const button = (
    <Box
      onClick={() => !disabled && onNav(item)}
      role="button"
      aria-disabled={disabled || undefined}
      aria-current={active ? 'page' : undefined}
      tabIndex={disabled ? -1 : 0}
      onKeyDown={(e) => {
        if (disabled) return;
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onNav(item);
        }
      }}
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: collapsed ? 'center' : 'flex-start',
        gap: collapsed ? 0 : 1.25,
        px: collapsed ? 1 : 1.5,
        py: 1.1,
        mx: collapsed ? 0.75 : 1.25,
        borderRadius: `${INV_RADIUS.control}px`,
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.45 : 1,
        position: 'relative',
        backgroundColor: active ? 'rgba(255,255,255,0.12)' : 'transparent',
        transition: 'all 0.15s ease',
        '&:hover': disabled ? {} : {
          backgroundColor: active ? 'rgba(255,255,255,0.14)' : 'rgba(255,255,255,0.06)',
        },
        ...(active && !collapsed && {
          '&::before': {
            content: '""',
            position: 'absolute',
            left: -4,
            top: '20%',
            bottom: '20%',
            width: 3,
            borderRadius: '0 3px 3px 0',
            backgroundColor: '#fff',
          },
        }),
      }}
    >
      <NavIcon sx={{
        fontSize: 20,
        color: active ? '#fff' : IPD_SIDEBAR.text,
        flexShrink: 0,
      }} />
      {!collapsed && (
        <>
          <Typography sx={{
            fontSize: 13.5,
            fontWeight: active ? 600 : 500,
            color: active ? '#fff' : IPD_SIDEBAR.text,
            lineHeight: 1.3,
            minWidth: 0,
          }}>
            {item.label}
          </Typography>
          {!disabled && badge > 0 && (
            <Box
              component="span"
              sx={{
                ml: 'auto',
                px: 0.75,
                py: 0.15,
                borderRadius: '8px',
                fontSize: 11,
                fontWeight: 700,
                lineHeight: 1.4,
                flexShrink: 0,
                backgroundColor: active ? '#fff' : 'rgba(255,255,255,0.18)',
                color: active ? IPD_COLORS.primaryDark : '#fff',
              }}
            >
              {badge}
            </Box>
          )}
        </>
      )}
    </Box>
  );

  if (collapsed) {
    const title = badge > 0 && !disabled ? `${item.label} (${badge})` : item.label;
    return (
      <Tooltip title={title} placement="right" arrow>
        <Box component="span" sx={{ display: 'block' }}>{button}</Box>
      </Tooltip>
    );
  }
  return button;
}

const groupLabelSx = {
  px: 2.5,
  pb: 0.75,
  fontSize: 10,
  fontWeight: 600,
  color: IPD_SIDEBAR.label,
  letterSpacing: '0.08em',
  textTransform: 'uppercase',
};

function SidebarContent({
  navConfig, activeId, badges, onNav, collapsed, onToggleCollapse, showCollapseToggle, sidebarExtra,
}) {
  const ModuleIcon = navConfig.icon;

  return (
    <Box sx={{
      width: '100%',
      height: '100%',
      background: `linear-gradient(180deg, ${IPD_SIDEBAR.bg} 0%, ${IPD_SIDEBAR.bgGradientEnd} 100%)`,
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
    }}>
      <Box sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: collapsed ? 'center' : 'space-between',
        gap: 1,
        px: collapsed ? 1.25 : 2,
        py: 1.75,
        minHeight: 60,
        borderBottom: `1px solid ${IPD_SIDEBAR.border}`,
        flexShrink: 0,
      }}>
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.25,
          minWidth: 0,
          justifyContent: collapsed ? 'center' : 'flex-start',
        }}>
          <Box sx={{
            width: 36,
            height: 36,
            borderRadius: `${INV_RADIUS.control}px`,
            backgroundColor: IPD_SIDEBAR.active,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}>
            <ModuleIcon sx={{ color: '#fff', fontSize: 19 }} />
          </Box>
          {!collapsed && (
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ fontWeight: 700, fontSize: 14, color: '#fff', lineHeight: 1.25 }} noWrap>
                {navConfig.title}
              </Typography>
              <Typography sx={{ fontSize: 11, color: IPD_SIDEBAR.label, lineHeight: 1.25 }} noWrap>
                {navConfig.subtitle}
              </Typography>
            </Box>
          )}
        </Box>
        {showCollapseToggle && !collapsed && (
          <Tooltip title="Collapse menu" placement="right">
            <IconButton
              size="small"
              onClick={onToggleCollapse}
              aria-label="Collapse sidebar"
              sx={{
                color: IPD_SIDEBAR.label,
                '&:hover': { color: '#fff', backgroundColor: 'rgba(255,255,255,0.08)' },
              }}
            >
              <ChevronLeftIcon sx={{ fontSize: 20 }} />
            </IconButton>
          </Tooltip>
        )}
      </Box>

      {!collapsed && sidebarExtra}

      <Box sx={{
        py: 1.5,
        flex: 1,
        minHeight: 0,
        overflowY: 'auto',
        overflowX: 'hidden',
        ...(collapsed ? hideScrollbarSx : thinScrollbarSx),
      }}>
        {navConfig.groups.map((group, gi) => (
          <Box
            key={group.id}
            role="group"
            aria-labelledby={group.label && !collapsed ? `inv-nav-grp-${group.id}` : undefined}
          >
            {group.label && !collapsed && (
              <Typography id={`inv-nav-grp-${group.id}`} sx={{ ...groupLabelSx, pt: gi === 0 ? 0 : 1.75 }}>
                {group.label}
              </Typography>
            )}
            {group.label && collapsed && (
              // Collapsed rail has no room for a header; a hairline keeps the grouping readable.
              <Box aria-hidden sx={{ height: '1px', mx: 1.75, my: 1.25, backgroundColor: IPD_SIDEBAR.border }} />
            )}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
              {group.items.map((item) => (
                <NavItem
                  key={item.id}
                  item={item}
                  active={item.id === activeId}
                  collapsed={collapsed}
                  badge={item.badgeKey ? badges?.[item.badgeKey] : undefined}
                  onNav={onNav}
                />
              ))}
            </Box>
          </Box>
        ))}
      </Box>

      {navConfig.footer?.length > 0 && (
        <Box sx={{ pt: 1, pb: 1, borderTop: `1px solid ${IPD_SIDEBAR.border}`, flexShrink: 0 }}>
          {!collapsed && <Typography sx={{ ...groupLabelSx, pt: 0.5 }}>Switch to</Typography>}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
            {navConfig.footer.map((item) => (
              <NavItem key={item.id} item={item} active={false} collapsed={collapsed} onNav={onNav} />
            ))}
          </Box>
        </Box>
      )}

      {showCollapseToggle && collapsed && (
        <Box sx={{ p: 1.25, borderTop: `1px solid ${IPD_SIDEBAR.border}`, flexShrink: 0 }}>
          <Tooltip title="Expand menu" placement="right">
            <IconButton
              onClick={onToggleCollapse}
              aria-label="Expand sidebar"
              sx={{
                width: '100%',
                color: IPD_SIDEBAR.text,
                borderRadius: `${INV_RADIUS.control}px`,
                '&:hover': { color: '#fff', backgroundColor: 'rgba(255,255,255,0.08)' },
              }}
            >
              <MenuIcon sx={{ fontSize: 20 }} />
            </IconButton>
          </Tooltip>
        </Box>
      )}
    </Box>
  );
}

function AppTopBar({ onMenuClick, moduleLabel, extra }) {
  return (
    <Box sx={{
      display: 'flex',
      alignItems: 'center',
      gap: 1.25,
      px: { xs: 2, sm: 2.5 },
      minHeight: 52,
      backgroundColor: IPD_COLORS.cardBg,
      borderBottom: `1px solid ${IPD_COLORS.border}`,
      flexShrink: 0,
    }}>
      <IconButton
        onClick={onMenuClick}
        aria-label="Open navigation"
        sx={{
          color: IPD_COLORS.primary,
          borderRadius: `${INV_RADIUS.control}px`,
          width: 36,
          height: 36,
          '&:hover': { backgroundColor: IPD_COLORS.selectedBg },
        }}
      >
        <MenuIcon sx={{ fontSize: 20 }} />
      </IconButton>
      <Typography sx={{ fontSize: 14, fontWeight: 500, color: IPD_COLORS.textSecondary }}>
        {moduleLabel}
      </Typography>
      {extra}
    </Box>
  );
}

/**
 * Grouped module sidebar + content shell.
 *
 * Presentation only — the caller resolves which module's config to pass and supplies
 * badge counts. `navConfig` shape lives in inventory/navigation.js.
 */
export default function ModuleSidebarLayout({
  navConfig,
  suiteBase,
  badges,
  children,
  storageKey = STORAGE_KEY,
  disableContentPadding = false,
  sidebarExtra,
  topBarExtra,
}) {
  const history = useHistory();
  const { pathname } = useLocation();
  // Raw query so this shell works outside Inventory's ThemeProvider (lab/radiology).
  // 900px is MUI's default `md` — same cutoff inventory used via theme.breakpoints.
  const isMobile = useMediaQuery('(max-width:899.95px)');

  const moduleBase = `${suiteBase}${navConfig.base}`;
  const activeId = resolveActiveNavId(navConfig, moduleBase, pathname);
  const focusMode = isFocusRoute(pathname);
  const showPersistentSidebar = !isMobile && !focusMode;

  const [sidebarExpanded, setSidebarExpanded] = useState(() =>
    readExpandedPreference(storageKey),
  );
  const [drawerOpen, setDrawerOpen] = useState(false);

  const persistExpanded = useCallback((expanded) => {
    setSidebarExpanded(expanded);
    try { sessionStorage.setItem(storageKey, String(expanded)); } catch { /* ignore */ }
  }, [storageKey]);

  useEffect(() => {
    setDrawerOpen(false);
  }, [pathname]);

  function onNav(item) {
    if (item.href) {
      history.push(item.href);
    } else if (item.module) {
      history.push(`${suiteBase}${NAV_MODULES[item.module].base}`);
    } else {
      history.push(`${moduleBase}${item.path}`);
    }
    setDrawerOpen(false);
  }

  function handleTopBarMenu() {
    if (showPersistentSidebar) {
      persistExpanded(true);
    } else {
      setDrawerOpen(true);
    }
  }

  const sidebarWidth = sidebarExpanded ? SIDEBAR_EXPANDED : SIDEBAR_COLLAPSED;
  const showTopBar = isMobile || focusMode || (showPersistentSidebar && !sidebarExpanded);

  const sidebarProps = {
    navConfig,
    activeId,
    badges,
    onNav,
    sidebarExtra,
  };

  return (
    <Box sx={{ display: 'flex', alignItems: 'flex-start', minHeight: '100vh', backgroundColor: IPD_COLORS.pageBg }}>
      {showPersistentSidebar && (
        <Box
          component="nav"
          aria-label={`${navConfig.title} menu`}
          sx={{
            width: sidebarWidth,
            flexShrink: 0,
            // Sticky + a bounded height is what lets the menu scroll internally.
            // Without it a 15-item menu just makes the page taller.
            position: 'sticky',
            top: 0,
            alignSelf: 'flex-start',
            height: '100dvh',
            transition: 'width 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
            overflow: 'hidden',
            boxShadow: '2px 0 12px rgba(13,35,63,0.06)',
          }}
        >
          <SidebarContent
            {...sidebarProps}
            collapsed={!sidebarExpanded}
            onToggleCollapse={() => persistExpanded(!sidebarExpanded)}
            showCollapseToggle
          />
        </Box>
      )}

      {!showPersistentSidebar && (
        <Drawer
          variant="temporary"
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          ModalProps={{ keepMounted: true }}
          PaperProps={{
            sx: {
              width: SIDEBAR_EXPANDED,
              background: `linear-gradient(180deg, ${IPD_SIDEBAR.bg} 0%, ${IPD_SIDEBAR.bgGradientEnd} 100%)`,
              borderRight: `1px solid ${IPD_SIDEBAR.border}`,
              boxSizing: 'border-box',
            },
          }}
          sx={{ '& .MuiBackdrop-root': { backgroundColor: 'rgba(13, 35, 63, 0.35)' } }}
        >
          <SidebarContent {...sidebarProps} collapsed={false} showCollapseToggle={false} />
        </Drawer>
      )}

      <Box
        component="main"
        sx={{
          flex: 1,
          minWidth: 0,
          minHeight: '100vh',
          backgroundColor: IPD_COLORS.pageBg,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {showTopBar && (
          <AppTopBar onMenuClick={handleTopBarMenu} moduleLabel={navConfig.title} extra={topBarExtra} />
        )}
        <Box sx={{ flex: 1, minWidth: 0, ...(disableContentPadding ? {} : { p: { xs: 2, sm: 2.5, md: 3 } }) }}>
          {children}
        </Box>
      </Box>
    </Box>
  );
}
